import React, {useCallback, useMemo, useState} from 'react';
import {Alert, FlexColumn, Label, Modal, Text, TextField} from '@forecast-it/design-system';
import {FormattedMessage, useIntl} from 'react-intl';
import {MODAL_TYPE, showModal} from '../generic_modal_conductor';
import {createToast} from '../../toasts/toast';
import PortalApproverPicker from '../../pickers/PortalApproverPicker';
import {usePortalEligibleApprovers} from '../../../hooks/usePortalEligibleApprovers';
import {resolveSignoffApproverLabel} from '../../../util/phaseSignoffStatus';
import {reassignPhaseSignoffApprover} from './reassignPhaseSignoffApprover';

export type ReassignSignoffApproverModalPayload = {
	/** Relay global id of the phase whose OPEN request is being handed over. */
	phaseId: string;
	/** Relay global id of the parent project — anchors the eligible-approver query. */
	projectId: string;
	phaseName: string;
	/**
	 * The current approver on the live request (portal_users UUID) — the one being
	 * replaced. Shown for context, and excluded from the picker so the PM cannot
	 * silently pick the same person and think it was reassigned.
	 */
	currentApproverPortalUserId: string | null;
};

type Props = ReassignSignoffApproverModalPayload & {closeModal: () => void};

/**
 * Hands a phase's OPEN client sign-off request to a different client workspace
 * user — when the original approver is away, has left, or was the wrong pick.
 *
 * The current approver is named at the top so the PM can see who they are
 * replacing, and dropped from the picker's options: reassignment to the same
 * person is not a state this operation supports (the backend refuses it, and
 * the PM would learn that only on submit). The reason is required — server
 * side and here, because the audit row exists to answer "why", and a row that
 * says nothing is worse than no row.
 *
 * The request stays in *Awaiting client*: a reassignment moves the approver,
 * not the deadline or the requested-at moment. The Sign-off Status cell
 * re-renders off the mutation's own Relay merge, so nothing needs to be
 * re-fetched here.
 */
export const ReassignSignoffApproverModal = ({
	closeModal,
	phaseId,
	projectId,
	phaseName,
	currentApproverPortalUserId,
}: Props): JSX.Element => {
	const {formatMessage} = useIntl();
	const {approvers, loading, error} = usePortalEligibleApprovers(projectId);

	const [approver, setApprover] = useState<string | null>(null);
	const [reason, setReason] = useState<string>('');
	const [submitting, setSubmitting] = useState(false);
	// A recognised refusal renders inline so the composed approver and reason
	// survive; the shared warning modal would replace this one and lose them.
	const [errorMessageId, setErrorMessageId] = useState<string | null>(null);

	// The picker options are the eligible list with the current approver removed.
	// A current approver who has already lost eligibility is absent from the list
	// anyway, so filtering is a no-op in that case rather than a special path.
	const offeredApprovers = useMemo(
		() => (currentApproverPortalUserId ? approvers.filter(a => a.portalUserId !== currentApproverPortalUserId) : approvers),
		[approvers, currentApproverPortalUserId]
	);

	// Resolved off the eligible list, not persisted here. A current approver who
	// is no longer eligible has no name to render, so we fall back to a generic
	// label rather than showing a bare UUID.
	const currentApproverLabel = resolveSignoffApproverLabel(currentApproverPortalUserId, approvers);

	const trimmedReason = reason.trim();
	const canSubmit = !!approver && trimmedReason.length > 0 && !submitting;

	const handleSubmit = useCallback(() => {
		if (!approver || trimmedReason.length === 0 || submitting) return;
		setSubmitting(true);
		setErrorMessageId(null);
		reassignPhaseSignoffApprover(
			{psaMilestoneId: phaseId, approverPortalUserId: approver, reason: trimmedReason},
			() => {
				createToast({
					duration: 5000,
					variant: 'banner',
					message: (
						<Alert
							type={Alert.TYPE.SUCCESS}
							text={formatMessage({id: 'reassign_approver_modal.reassigned_toast'}, {name: phaseName})}
						/>
					),
				});
				closeModal();
			},
			validationMessageId => {
				setSubmitting(false);
				setErrorMessageId(validationMessageId);
			}
		);
	}, [approver, trimmedReason, submitting, phaseId, phaseName, closeModal, formatMessage]);

	return (
		<Modal onCloseModal={closeModal} closeOnInteractOutside={false}>
			<Modal.Title>{formatMessage({id: 'reassign_approver_modal.title'})}</Modal.Title>
			<Modal.Content data-cy="reassign-approver-modal">
				<FlexColumn gap="m">
					<Text color="medium">{formatMessage({id: 'reassign_approver_modal.subtitle'}, {name: phaseName})}</Text>
					<FlexColumn gap="xs">
						<Label>{formatMessage({id: 'reassign_approver_modal.current_approver_heading'})}</Label>
						<div data-testid="reassign-approver-current" data-cy="reassign-approver-current">
							<Text>
								{currentApproverLabel ??
									formatMessage({id: 'reassign_approver_modal.current_approver_unknown'})}
							</Text>
						</div>
					</FlexColumn>
					<FlexColumn gap="xs">
						<Label>{formatMessage({id: 'reassign_approver_modal.new_approver_heading'})}</Label>
						<PortalApproverPicker
							value={approver}
							approvers={offeredApprovers}
							loading={loading}
							error={error}
							onChange={setApprover}
							placeholder={formatMessage({id: 'reassign_approver_modal.new_approver_placeholder'})}
							ariaLabel={formatMessage({id: 'reassign_approver_modal.new_approver_heading'})}
							dataCy="reassign-approver-picker"
						/>
					</FlexColumn>
					<FlexColumn gap="xs">
						<Label>{formatMessage({id: 'reassign_approver_modal.reason_heading'})}</Label>
						<TextField
							value={reason}
							onValueChange={(value: string) => setReason(value)}
							placeholder={formatMessage({id: 'reassign_approver_modal.reason_placeholder'})}
							aria-label={formatMessage({id: 'reassign_approver_modal.reason_heading'})}
							maxLength={500}
							data-cy="reassign-approver-reason"
						/>
						<Text color="medium">{formatMessage({id: 'reassign_approver_modal.reason_note'})}</Text>
					</FlexColumn>
					{errorMessageId && (
						<div data-testid="reassign-approver-error" data-cy="reassign-approver-error">
							<Alert type={Alert.TYPE.ERROR} text={formatMessage({id: errorMessageId})} />
						</div>
					)}
				</FlexColumn>
			</Modal.Content>
			<Modal.Footer>
				<Modal.SecondaryFooterButton onPress={closeModal} dataCy="reassign-approver-cancel">
					<FormattedMessage id="common.cancel" />
				</Modal.SecondaryFooterButton>
				<Modal.PrimaryFooterButton onPress={handleSubmit} disabled={!canSubmit} dataCy="reassign-approver-confirm">
					{formatMessage({id: 'reassign_approver_modal.submit'})}
				</Modal.PrimaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export const showReassignSignoffApproverModal = (payload: ReassignSignoffApproverModalPayload): void => {
	showModal({type: MODAL_TYPE.REASSIGN_SIGNOFF_APPROVER, ...payload});
};
